import { Button, Spinner, Table } from '../../../ui';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import DefaultLayout from '../../components/DefaultLayout';
import { StyledBannersPage } from './style';
import { EditBannerDrawer } from '../../../ui/Drawer/Drawers/EditBannerDrawer';
import { BannerProps, ImageProps } from '../../../types/BannerData';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { FallbackImage } from '../../../ui/UploadImage/fallBack/FallBackImage';
import dayjs from 'dayjs';
import { StyledImagePreviews } from 'ui/UploadImage/Uploader/style';

export const BannersPage = () => {
  const [isOpenDrawer, setIsOpenDrawer] = useState<boolean>(false);
  const [bannerToEdit, setBannerToEdit] = useState<BannerProps | null>(null);
  const [banners, setBanners] = useState<BannerProps[] | null>(null);

  const intl = useIntl();

  const { isLoading: isBannersLoading, refetch: refetchBanners } =
    useQueryApiClient({
      request: {
        url: '/api/v2/banners',
        method: 'GET',
      },
      onSuccess: (response) => {
        setBanners(response.data);
      },
    });

  const handleEdit = useCallback((value: BannerProps) => {
    setBannerToEdit(value);
    setIsOpenDrawer(true);
  }, []);

  const handleClose = () => {
    refetchBanners();
    setBannerToEdit(null);
    setIsOpenDrawer(false);
  };

  const Actions = useCallback(
    (record: BannerProps) => (
      <Button
        label={intl.formatMessage({ id: 'general.edit' })}
        onClick={() => handleEdit(record)}
      />
    ),
    [handleEdit, intl]
  );

  const ImagePreviews = useCallback(
    (images: ImageProps[], banner: BannerProps) => (
      <StyledImagePreviews>
        {banner.images.length
          ? images.map((image) => {
              if (!image.url_original) {
                return null;
              }

              return (
                <FallbackImage
                  key={image.url_original || image.id}
                  src={image.url_original}
                  type={banner?.type}
                  showDefault={false}
                />
              );
            })
          : null}
      </StyledImagePreviews>
    ),
    []
  );

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'general.name' }),
        dataIndex: 'title',
        render: (value: string) => value,
      },
      {
        title: intl.formatMessage({ id: 'banners.count' }),
        dataIndex: 'images',
        render: (value: ImageProps[], banner: BannerProps) => value.length,
      },
      {
        title: intl.formatMessage({ id: 'banners.images' }),
        dataIndex: 'images',
        render: (value: ImageProps[], banner: BannerProps) =>
          ImagePreviews(value, banner),
      },
      {
        title: intl.formatMessage({ id: 'general.updated_at' }),
        dataIndex: 'updated_at',
        render: (value: string) => value && dayjs(value).format('DD.MM.YYYY'),
      },
      {
        title: intl.formatMessage({ id: 'general.actions' }),
        render: (_: any, record: BannerProps) => Actions(record),
      },
    ],
    [Actions, ImagePreviews, intl]
  );

  return (
    <DefaultLayout.PageLayout
      breadcrumbs={[
        {
          title: intl.formatMessage({ id: 'navigation.beginning' }),
        },
        {
          title: intl.formatMessage({ id: 'navigation.banners' }),
        },
      ]}
    >
      <Spinner spinning={isBannersLoading}>
        <DefaultLayout.PageContent>
          <StyledBannersPage>
            <Table
              columns={columns}
              dataSource={banners ? banners : undefined}
            />
            <EditBannerDrawer
              open={isOpenDrawer}
              onClose={handleClose}
              bannerToEdit={bannerToEdit ? bannerToEdit : null}
            />
          </StyledBannersPage>
        </DefaultLayout.PageContent>
      </Spinner>
    </DefaultLayout.PageLayout>
  );
};
